import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Button, Container, Dimmer, Form, Icon, Loader, Pagination } from 'semantic-ui-react';
import ImageOnS3 from '../../../components/imageOnS3/imageOnS3.component';
import './shelf.admin.scss';
import { ShelfModel } from './ShelfModel';

function AdminShelfComponent() {
  const history = useHistory();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchValue, setSearchValue] = useState("");
  const [searchResult, setSearchResult] = useState([] as ShelfModel[]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [listPage, setListPage] = useState(1);
  const [pageMeta, setPageMeta] = useState({ totalRows: 0, totalPages: 0, size: 10 });

  // Initialize search value from URL query params and trigger search
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    if (searchParam && searchParam.length > 5) {
      setSearchValue(searchParam);
      // Trigger search automatically when recovering from URL
      setTimeout(() => {
        performSearch(searchParam);
      }, 100);
    }
    window.scrollTo(0,0)
  }, []); // Only run on mount, not on every location.search change

  useEffect(() => {
        // Use setTimeout to ensure the component is fully rendered
        const timer = setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const searchHandler = (e: any) => {
    const value = e.target.value;
    setSearchValue(value);
    
    // Update URL query params
    const urlParams = new URLSearchParams(location.search);
    if (value.trim() && value.trim().length > 5) {
      urlParams.set('search', value);
    } else {
      urlParams.delete('search');
    }
    
    const newSearch = urlParams.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    history.replace(newUrl);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // If value is shorter than 6 characters, wait 2 seconds before searching
    if (value.length > 0 && value.length < 6) {
      const timeout = setTimeout(() => {
        performSearch(value);
      }, 2000);
      setSearchTimeout(timeout);
    } else if (value.length >= 6) {
      // Search immediately for 6+ characters
      performSearch(value);
    } else {
      // Clear results if input is empty
      setSearchResult([]);
      setHasSearched(false);
      setErrorMsg("");
    }
  }

  const performSearch = (searchTerm: string, pageNum = 1) => {
    var token = localStorage.getItem('adminToken');

    if (!token || token === 'undefined') {
      console.log("Will redirect to: " + window.location.pathname);
      history.push("/mongol/login");
      return;
    }

    if (!searchTerm.trim()) {
      setErrorMsg("Хайх утга оруулна уу");
      return;
    }

    setLoading(true);
    setErrorMsg("");
    setHasSearched(true);

    fetch(process.env.REACT_APP_API_URL + 'mongol/warehouse/products', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ searchValue: searchTerm, page: { page: pageNum, size: 10 } })
    })
      .then((res: any) => {
        setLoading(false);
        if (res.ok) {
          res.json().then((resJson: any) => {
            if (resJson?.data) {
              setSearchResult(resJson.data);
            } else {
              setSearchResult([]);
            }
            if (resJson?.page) {
              const p = resJson.page;
              setListPage(p.page);
              setPageMeta({
                totalRows: p.totalRows ?? 0,
                totalPages: p.totalPages ?? 0,
                size: p.size ?? 10,
              });
            } else {
              setListPage(1);
              setPageMeta({ totalRows: 0, totalPages: 0, size: 10 });
            }
          });
        } else {
          setErrorMsg("Сүлжээнд алдаа гарлаа");
        }
      })
      .catch(err => {
        setLoading(false);
        setErrorMsg("Сүлжээний алдаа");
        console.error(err);
      });
  }

  const search = () => {
    performSearch(searchValue);
  }

  const clearSearch = () => {
    setSearchValue("");
    setSearchResult([]);
    setHasSearched(false);
    setErrorMsg("");
    setListPage(1);
    setPageMeta({ totalRows: 0, totalPages: 0, size: 10 });
    
    // Clear URL parameters
    const urlParams = new URLSearchParams(location.search);
    urlParams.delete('search');
    const newSearch = urlParams.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    history.replace(newUrl);
  }

  return (
    <div className="page-admin">
      <Container fluid>
        <Dimmer active={loading}>
          <Loader size='medium'></Loader>
        </Dimmer>

        <div className="flex-div width-100">
          <section className="admin-shelf-search" id="my-form">
            <div className="form-group">
              <h2>Бүтээгдэхүүн хайх</h2>
            </div>

            <Form>
              <Form.Group>
                {errorMsg ? <div className="error-msg"><Icon name="warning circle" />{errorMsg}</div> : null}
              </Form.Group>

              <Form.Group widths='equal'>
                <div className="input-container">
                  <Form.Input
                    ref={inputRef}
                    type="text"
                    name="searchValue"
                    value={searchValue}
                    onChange={searchHandler}
                    placeholder="Бар код эсвэл барааны нэр оруулна уу"
                    autoFocus
                  />
                  {searchValue && (
                    <Icon
                      name="times"
                      circular
                      link
                      className="clear-button"
                      onClick={clearSearch}
                    />
                  )}
                </div>
              </Form.Group>

              <Form.Group>
                <Form.Field className='width-100'>
                  <Button
                    fluid
                    onClick={search}
                    disabled={loading}
                  >
                    Хайх
                  </Button>
                </Form.Field>
              </Form.Group>
            </Form>

            {hasSearched && !loading && (
              <div className="search-results">
                {searchResult && searchResult.length > 0 ? (
                  <div className="search-result-item">
                    <h3>
                      Олдсон бүтээгдэхүүн
                      {pageMeta.totalRows > 0
                        ? ` (нийт ${pageMeta.totalRows} — ${(listPage - 1) * pageMeta.size + 1}–${Math.min(
                            listPage * pageMeta.size,
                            pageMeta.totalRows
                          )} харуулж байна):`
                        : ` (${searchResult.length}):`}
                    </h3>
                    {searchResult.map((item, index) => (
                      <div key={item.id || index} className="result-details">
                        <p><strong>Бүтээгдэхүүний ID:</strong> <Link className="underline" target="_blank" to={"/mongol/product/" + item.productId}>{item.productId}</Link></p>
                        <p><strong>Нэр:</strong> {item.name}</p>
                        <p><strong>Баркод:</strong> {item.barCode}</p>
                        <p><strong>Үлдэгдэл:</strong> {item.count}</p>
                        <p><strong>Анхааруулах үлдэгдэл:</strong> {item.minCount}</p>
                        {item.slug && (
                        <p><strong>Линк:</strong> <a className='underline' href={"/product/" + item.slug} target="_blank" rel="noopener noreferrer">Худалдааны линк</a></p>
                        )}
                        <br/>
                        <p>
                        <ImageOnS3 imgKey={item.imageXsUrl} width='100' height='100'/>
                        </p>

                        {(Number(item.hasImport) || 0) > 0 ? (
                          <Link
                            className="ui button red"
                            to={
                              "/mongol/warehouse/" +
                              item.warehouseId +
                              "?from=shelf&barcode=" +
                              encodeURIComponent(searchValue)
                            }
                          >
                            ҮЛДЭГДЭЛ ӨӨРЧЛӨХ
                          </Link>
                        ) : (
                          <Link
                            className="ui button red"
                            to={"/mongol/warehouse/" + item.warehouseId + "/count?from=products"}
                            title="Импорт хийгдээгүй тул count хуудсаар засна"
                          >
                            ҮЛДЭГДЭЛ ӨӨРЧЛӨХ
                          </Link>
                        )}

                        {item?.barCode ? null : 
                          <Link className="ui button" to={"/mongol/product/" + item.productId}>
                            <Icon name="qrcode" />
                            БАРКОД ҮҮСГЭХ
                          </Link>
                        }
                        {index < searchResult.length - 1 && <hr style={{margin: '20px 0'}} />}
                      </div>
                    ))}
                    {pageMeta.totalPages > 1 && (
                      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                          totalPages={pageMeta.totalPages}
                          activePage={listPage}
                          onPageChange={(_e, { activePage }) => {
                            const p = activePage == null ? null : Number(activePage);
                            if (p != null && !Number.isNaN(p)) performSearch(searchValue, p);
                          }}
                          firstItem={null}
                          lastItem={null}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-results">
                    <Icon name="search" size="large" />
                    <p>Бүтээгдэхүүн олдсонгүй</p>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </Container>
    </div>
  );
}

export default AdminShelfComponent;